import React, { useState } from 'react';
import clsx from 'clsx';

import { useCheckAccess } from '@hooks';

type TabsProps<T extends string = string> = {
  tabs: TabType<T>[];
  className?: string;
  tabsGrow?: boolean;
  tabClassName?: string;
  tabsClassName?: string;
  contentClassName?: string;
  activeTab?: string;
  keepMounted?: boolean;
  defaultActiveTab?: string;
  size?: 'small' | 'medium' | 'big';
  onChange?: (newTab: TabType<T>['name']) => void;
  children?: React.ReactNode | React.ReactNode[];
};

type TabProps = {
  name: string;
  children?: React.ReactNode;
};

const sizeMap: { [x in NonNullable<TabsProps['size']>]: string } = {
  small: 'py-xs px-xs text-tiny',
  medium: 'py-sm px-sm text-small',
  big: 'py-sm px-md text-medium',
};

export const Tab: React.FC<TabProps> = ({ children }) => {
  return <>{children}</>;
};

const Tabs = <T extends string = string>({
  activeTab: value,
  defaultActiveTab,
  tabs: initialTabs,
  onChange,
  children,
  className,
  keepMounted = false,
  tabClassName,
  tabsClassName,
  tabsGrow = false,
  contentClassName,
  size = 'medium',
}: TabsProps<T>) => {
  const tabs = useCheckAccess(initialTabs);

  const [localActiveTab, setActiveTab] = useState(
    defaultActiveTab ?? tabs?.[0]?.name
  );
  const activeTab = value ?? localActiveTab;

  const handleChangeTab = (newTab: T) => {
    onChange?.(newTab);
    setActiveTab(newTab);
  };

  const Content = !keepMounted
    ? React.Children.toArray(children)
        .map(c => c as React.ReactElement<TabProps>)
        .filter(c => c?.type === Tab && c.props.name === activeTab)
        .map((c, index) => (
          <React.Fragment key={c.props.name ?? index}>{c}</React.Fragment>
        ))
    : React.Children.toArray(children)
        .map(c => c as React.ReactElement<TabProps>)
        .map((c, index) => (
          <div
            key={c.props.name ?? index}
            className={clsx(
              c.props.name === activeTab ? 'block' : 'hidden',
              contentClassName
            )}
          >
            {c}
          </div>
        ));

  if (tabs.length === 0) return null;

  return (
    <div className={clsx('flex flex-col flex-1', className)}>
      <div className={clsx(tabsClassName, 'flex grow-0 shrink-0 rounded-md')}>
        {tabs.map((tab, index) => (
          <div
            key={`${tab.name}-${index}`}
            onClick={() => handleChangeTab(tab.name)}
            className={clsx(
              tabClassName,
              'cursor-pointer transition-def',
              'text-center',
              'border border-solid',
              !index && 'rounded-l-md',
              tabsGrow ? 'grow' : '',
              sizeMap[size] || size,
              index === tabs.length - 1 && 'rounded-r-md',
              activeTab === tab.name
                ? 'border-main outline outline-main z-[1]'
                : ''
            )}
          >
            {tab.label}
          </div>
        ))}
      </div>
      {keepMounted ? (
        Content
      ) : (
        <div className={contentClassName}>{Content}</div>
      )}
    </div>
  );
};

Tabs.Tab = Tab;

export default Tabs;
